/**
 * Settings Page - Application Preferences
 *
 * Provides user-configurable application settings including:
 * - Notification preferences
 * - Privacy controls
 * - Display preferences
 * - Data management
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BellIcon,
  ShieldCheckIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon,
  LockClosedIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../layouts';
import { useMetaTags } from '../hooks/useMetaTags';
import { entitlementService, type EntitlementStatus } from '../services/entitlement';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';

interface UserPreferences {
  notificationsEnabled: boolean;
  dataRetentionDays: number; // 0 = keep forever
  allowAnonymousDataUsage: boolean;
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  useMetaTags({
    title: 'Settings | Smart Prompt',
    url: 'https://www.smartprompt.us/settings'
  });
  const paywallEnabled = useFeatureFlag('PAYWALL');
  const isBetaEnabled = useFeatureFlag('BETA');

  // User preferences (combined notifications and data usage)
  const [preferences, setPreferences] = useState<UserPreferences>({
    notificationsEnabled: true,
    dataRetentionDays: 30, // default to 30 days (Edge tier default)
    allowAnonymousDataUsage: false,
  });

  // Entitlement status for plan limits
  const [entitlementStatus, setEntitlementStatus] = useState<EntitlementStatus | null>(null);

  // UI states
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');


  // Fetch entitlements on mount
  useEffect(() => {
    // If paywall is disabled, use unlimited defaults and skip fetching
    if (!paywallEnabled) {
      const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
      setEntitlementStatus(unlimitedDefaults);
      console.log('[SettingsPage] Paywall disabled - using unlimited defaults');
      return;
    }

    // Check cache first (synchronous) to avoid flash
    const cachedStatus = entitlementService.getCachedStatusSync();
    if (cachedStatus) {
      setEntitlementStatus(cachedStatus);
      console.log('[SettingsPage] Using cached entitlements:', cachedStatus.plan?.displayName || 'unknown plan');
    }

    const fetchEntitlements = async () => {
      try {
        const status = await entitlementService.getStatus();

        // If paywall is disabled in response, use unlimited defaults
        if (!status.paywallEnabled) {
          const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
          setEntitlementStatus(unlimitedDefaults);
          console.log('[SettingsPage] Paywall disabled in response - using unlimited defaults');
          return;
        }

        setEntitlementStatus(status);
      } catch (err) {
        console.error('[SettingsPage] Failed to fetch entitlements:', err);
        // Fail-open: use unlimited defaults on error
        const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
        setEntitlementStatus(unlimitedDefaults);
        console.log('[SettingsPage] Using unlimited defaults due to error');
      }
    };
    fetchEntitlements();
  }, [paywallEnabled]);

  // Load user preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      if (!user) return;

      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.user) {
            // Load preferences
            if (data.user.preferences) {
              const prefs = data.user.preferences;
              setPreferences({
                notificationsEnabled: prefs.notificationsEnabled ?? true,
                dataRetentionDays: prefs.dataRetentionDays ?? 30,
                allowAnonymousDataUsage: prefs.allowAnonymousDataUsage ?? false,
              });
            }
          }
        }
      } catch (err) {
        console.error('Failed to load preferences:', err);
      }
    }

    loadPreferences();
  }, [user]);

  const toggleTrackBase =
    'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-800';

  const toggleThumbBase = 'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-slate-200/80 transition-transform';

  // Handle notification toggle
  const handleNotificationsToggle = () => {
    setPreferences(prev => ({ ...prev, notificationsEnabled: !prev.notificationsEnabled }));
  };

  // Handle data retention change
  const handleDataRetentionChange = (days: number) => {
    setPreferences(prev => ({ ...prev, dataRetentionDays: days }));
  };

  // Handle anonymous data usage toggle
  const handleAnonymousDataToggle = () => {
    setPreferences(prev => ({ ...prev, allowAnonymousDataUsage: !prev.allowAnonymousDataUsage }));
  };

  // Handle save all preferences
  const handleSavePreferences = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setSaveError(null);
      setSaveSuccess(false);

      const token = await user.getIdToken();

      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preferences }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save preferences');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Save preferences failed:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  // Handle data export
  const handleExportData = async () => {
    if (!user) return;

    try {
      setExportLoading(true);
      setSaveError(null);
      const token = await user.getIdToken();

      console.log(`Starting ${exportFormat.toUpperCase()} export...`);
      const response = await fetch(`/api/auth/export-data?format=${exportFormat}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Export response received:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        contentDisposition: response.headers.get('content-disposition'),
      });

      if (!response.ok) {
        // Try to get detailed error message from response
        let errorMessage = 'Failed to export data. Please try again.';
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // If response is not JSON, use default message
        }
        console.error('Export failed with status:', response.status, errorMessage);
        throw new Error(errorMessage);
      }

      // Success - download the file
      // For JSON exports, Content-Type will be application/json
      // For CSV exports, Content-Type will be application/zip
      // Both are valid success responses when response.ok is true
      console.log('Export successful, creating download...');
      const blob = await response.blob();
      console.log('Blob created:', { size: blob.size, type: blob.type });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Set filename based on format
      const extension = exportFormat === 'csv' ? 'zip' : 'json';
      const filename = `smartprompt-data-${Date.now()}.${extension}`;
      a.download = filename;

      console.log('Triggering download:', filename);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('Export completed successfully');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Export failed with error:', err);
      console.error('Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        exportFormat,
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to export data. Please try again.';
      setSaveError(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-white">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
          <p className="text-slate-400">Manage your account preferences</p>
        </div>

        {/* Success/Error Messages */}
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg mb-6"
          >
            <CheckCircleIcon className="w-5 h-5 text-green-400" />
            <span className="text-green-300">Settings saved successfully!</span>
          </motion.div>
        )}

        {saveError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6"
          >
            <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{saveError}</span>
          </motion.div>
        )}

        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden mb-6">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <BellIcon className="w-5 h-5" />
              Notifications
            </h2>
            <p className="text-sm text-slate-400 mt-1">Manage how you receive notifications</p>
          </div>

          <div className="p-6 space-y-4">
            {/* Master Notifications Toggle */}
            <div className="flex items-center justify-between">
              <div>
                  <h3 className="text-white font-medium">Enable Notifications</h3>
                  <p className="text-sm text-slate-400">Receive updates about comparisons, features, and important alerts</p>
                </div>
                <button
                  onClick={handleNotificationsToggle}
                  className={`${toggleTrackBase} ${
                    preferences.notificationsEnabled ? 'bg-cyan-500 shadow-inner' : 'bg-slate-600'
                  }`}
                  aria-pressed={preferences.notificationsEnabled}
                  type="button"
                >
                  <span
                    className={`${toggleThumbBase} ${
                      preferences.notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <button
                onClick={handleSavePreferences}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>

        {/* Privacy & Data */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden mb-6">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <ShieldCheckIcon className="w-5 h-5" />
              Privacy & Data
            </h2>
            <p className="text-sm text-slate-400 mt-1">Manage your data and privacy preferences</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Data Export */}
            <div>
              <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                <DocumentArrowDownIcon className="w-5 h-5" />
                Export Your Data
              </h3>
              <p className="text-sm text-slate-400 mb-3">
                Download a complete copy of all your data including comparisons, responses, scores, analytics, and settings
              </p>

              {/* Format Selection */}
              <div className="mb-3">
                <label className="block text-sm text-slate-300 mb-2">Export Format</label>
                <div className="flex gap-3">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="json"
                      checked={exportFormat === 'json'}
                      onChange={() => setExportFormat('json')}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-white">JSON (single file)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={() => setExportFormat('csv')}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-white">CSV (ZIP archive)</span>
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {exportFormat === 'json'
                    ? 'Downloads a single JSON file with all your data in a structured format'
                    : 'Downloads a ZIP file containing multiple CSV files (user profile, comparisons, responses, scores, etc.)'}
                </p>
              </div>

              <button
                onClick={handleExportData}
                disabled={exportLoading}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <DocumentArrowDownIcon className="w-4 h-4" />
                {exportLoading ? 'Exporting...' : `Export as ${exportFormat.toUpperCase()}`}
              </button>
            </div>

          </div>
        </div>

        {/* Data Usage Preferences */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden mb-6">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5" />
              Data Usage Preferences
            </h2>
            <p className="text-sm text-slate-400 mt-1">Control how your data is stored and used</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Data Retention */}
            <div>
              <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                <ClockIcon className="w-5 h-5" />
                Data Retention
              </h3>
              <p className="text-sm text-slate-400 mb-3">
                Choose how long to keep your comparison history
              </p>

              <div className="space-y-2">
                {/* 1 day - Available for Edge, Flow, Nexus */}
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="dataRetention"
                    checked={preferences.dataRetentionDays === 1}
                    onChange={() => handleDataRetentionChange(1)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-white">
                    Auto-delete after 1 day
                  </span>
                </label>

                {/* 7 days - Available for Edge, Flow, Nexus */}
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="dataRetention"
                    checked={preferences.dataRetentionDays === 7}
                    onChange={() => handleDataRetentionChange(7)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-white">
                    Auto-delete after 7 days
                  </span>
                </label>

                {/* 30 days - Available for Edge, Flow, Nexus (Edge default) */}
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="dataRetention"
                    checked={preferences.dataRetentionDays === 30}
                    onChange={() => handleDataRetentionChange(30)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-white">
                    Auto-delete after 30 days
                  </span>
                </label>

                {/* Keep forever - Only for Nexus */}
                {(() => {
                  const planCode = entitlementStatus?.plan?.code || 'edge';
                  const isNexus = planCode === 'nexus';
                  const isLocked = !isNexus;

                  return (
                    <label className={`flex items-center space-x-3 ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="dataRetention"
                        checked={preferences.dataRetentionDays === 0}
                        onChange={() => !isLocked && handleDataRetentionChange(0)}
                        disabled={isLocked}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                      />
                      <span className={`${isLocked ? 'text-slate-500' : 'text-white'} flex items-center gap-2`}>
                        Keep my history until I delete it
                        {isLocked && <LockClosedIcon className="w-4 h-4 text-slate-500" />}
                      </span>
                    </label>
                  );
                })()}
              </div>

              {/* Upgrade CTA for non-Nexus users - Only show if PAYWALL is enabled */}
              {paywallEnabled && entitlementStatus && entitlementStatus.plan.code !== 'nexus' && (
                <p className="mt-3 text-xs text-slate-500">
                  Want to keep your comparison history forever?{' '}
                  <a
                    href="https://www.smartprompt.us/billing/upgrade"
                    className="text-slate-400 hover:text-slate-300 underline"
                  >
                    Upgrade to Nexus
                  </a>
                </p>
              )}
            </div>

            {/* Anonymous Data Usage */}
            {isBetaEnabled && (
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">Product Improvement</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Allow my prompts and results to be used to improve Smart Prompt (anonymized)
                    </p>
                  </div>
                  <button
                    onClick={handleAnonymousDataToggle}
                    className={`${toggleTrackBase} ${
                      preferences.allowAnonymousDataUsage ? 'bg-cyan-500 shadow-inner' : 'bg-slate-600'
                    }`}
                    aria-pressed={preferences.allowAnonymousDataUsage}
                    type="button"
                  >
                    <span
                      className={`${toggleThumbBase} ${
                        preferences.allowAnonymousDataUsage ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Your data will be anonymized and used only to improve our AI models and features. Personal information will never be shared.
                </p>
              </div>
            )}

            {/* Save Button */}
            <div className="pt-4">
              <button
                onClick={handleSavePreferences}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
